import * as XLSX from 'xlsx';
import { t } from '../localization';

/**
 * Экспортирует данные тикетов в файл Excel
 * @param {Array} tickets - массив тикетов для экспорта
 * @param {Array} projects - массив проектов для получения префиксов
 * @param {Object} filters - примененные фильтры
 */
export const exportTicketsToExcel = (tickets, projects, filters = {}) => {
  // Форматирование даты для отображения в Excel
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получение имени проекта по ID
  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : '';
  };

  // Получение префикса тикета
  const getTicketPrefix = (ticket) => {
    if (ticket.project && ticket.project.ticketPrefix) {
      return `${ticket.project.ticketPrefix}-${ticket.id}`;
    }
    
    const project = projects.find(p => p.id === ticket.projectId);
    return project && project.ticketPrefix 
      ? `${project.ticketPrefix}-${ticket.id}` 
      : `ID-${ticket.id}`;
  };

  // Собираем список кастомных полей, отмеченных для экспорта
  const exportableFields = [];
  const fieldIds = new Set();
  const usedHeaders = new Set();

  const buildHeader = (label, name, id) => {
    const base = label || name || `Поле ${id}`;
    let header = base;
    let counter = 2;
    while (usedHeaders.has(header)) {
      header = `${base} (${counter})`;
      counter += 1;
    }
    usedHeaders.add(header);
    return header;
  };

  tickets.forEach(ticket => {
    (ticket.customFieldValues || []).forEach(cf => {
      if (cf?.field?.exportToReport && !fieldIds.has(cf.field.id)) {
        fieldIds.add(cf.field.id);
        exportableFields.push({
          id: cf.field.id,
          label: cf.field.label,
          name: cf.field.name,
          type: cf.field.type,
          header: buildHeader(cf.field.label, cf.field.name, cf.field.id)
        });
      }
    });
  });

  exportableFields.sort((a, b) => (a.header || '').localeCompare(b.header || '', 'ru', { sensitivity: 'base' }));

  const formatCustomFieldValue = (type, value) => {
    if (value === null || value === undefined) return '';
    switch ((type || '').toUpperCase()) {
      case 'DATETIME': {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ru-RU');
      }
      case 'MULTISELECT':
      case 'LIST':
        return Array.isArray(value) ? value.join(', ') : String(value ?? '');
      case 'CHECKBOX':
        return value ? 'Да' : 'Нет';
      case 'TEXTAREA':
      case 'TEXT':
      default:
        return typeof value === 'string' ? value : String(value);
    }
  };

  // Подготовка данных для экспорта
  const exportData = tickets.map(ticket => {
    const baseRow = {
      'ID': getTicketPrefix(ticket),
      'Заголовок': ticket.title,
      'Описание': ticket.description ? ticket.description.replace(/<[^>]*>/g, '') : '',
      'Проект': getProjectName(ticket.projectId),
      'Статус': ticket.statusObj ? ticket.statusObj.name : ticket.status,
      'Тип': ticket.typeLabel || 'Неизвестно',
      'Приоритет': ticket.priorityLabel || ticket.priority,
      'Автор': ticket.createdBy ? ticket.createdBy.name : '',
      'Исполнитель': ticket.assignedTo ? ticket.assignedTo.name : '',
      'Дата создания': formatDate(ticket.createdAt),
      'План. дата решения': formatDate(ticket.plannedEndDate),
      'Дата обновления': formatDate(ticket.updatedAt)
    };

    if (exportableFields.length > 0) {
      const values = ticket.customFieldValues || [];
      exportableFields.forEach(field => {
        const found = values.find(v => v.fieldId === field.id || v.field?.id === field.id);
        baseRow[field.header] = formatCustomFieldValue(field.type, found ? found.value : null);
      });
    }

    return baseRow;
  });

  // Создание рабочей книги Excel
  const workbook = XLSX.utils.book_new();
  
  // Создание листа с данными
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  
  // Настройка ширины столбцов
  const columnWidths = [
    { wch: 15 }, // ID
    { wch: 40 }, // Заголовок
    { wch: 50 }, // Описание
    { wch: 20 }, // Проект
    { wch: 15 }, // Статус
    { wch: 15 }, // Тип
    { wch: 15 }, // Приоритет
    { wch: 20 }, // Автор
    { wch: 20 }, // Исполнитель
    { wch: 20 }, // Дата создания
    { wch: 20 }, // План. дата решения
    { wch: 20 }, // Дата обновления
  ];
  exportableFields.forEach(() => columnWidths.push({ wch: 25 }));
  worksheet['!cols'] = columnWidths;

  // Добавление листа в книгу
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Тикеты');

  // Формирование имени файла с учетом фильтров
  let fileName = 'tickets';
  
  // Добавление информации о фильтрах в имя файла
  if (filters.projectId) {
    const projectName = getProjectName(filters.projectId);
    fileName += `_project-${projectName.replace(/\s+/g, '_')}`;
  }
  
  if (filters.status) {
    fileName += `_status-${filters.status.name || filters.status}`;
  }
  
  if (filters.assigneeId) {
    fileName += '_assigned';
  }
  
  if (filters.authorId) {
    fileName += '_author';
  }

  if (filters.plannedDateFrom || filters.plannedDateTo) {
    const from = filters.plannedDateFrom ? filters.plannedDateFrom : 'start';
    const to = filters.plannedDateTo ? filters.plannedDateTo : 'end';
    fileName += `_planned-${from}-${to}`;
  }

  fileName += '.xlsx';

  // Экспорт файла
  XLSX.writeFile(workbook, fileName);
};
